<template>
  <Teleport to="body">
    <div
      v-show="isVisible"
      class="markdown-context-menu"
      :style="menuStyle"
      @click.stop
      @contextmenu.prevent.stop
    >
      <!-- With selection: show Cut, Copy, Delete -->
      <template v-if="hasSelectionValue">
        <button class="menu-item" @click="$emit('action', 'cut')">
          <span class="menu-icon">✂️</span>
          <span>{{ s__('ContextMenu|Cut') }}</span>
          <span class="menu-shortcut">Ctrl+X</span>
        </button>
        <button class="menu-item" @click="$emit('action', 'copy')">
          <span class="menu-icon">📋</span>
          <span>{{ s__('ContextMenu|Copy') }}</span>
          <span class="menu-shortcut">Ctrl+C</span>
        </button>
        <button class="menu-item" @click="$emit('action', 'delete')">
          <span class="menu-icon"><TrashIcon /></span>
          <span>{{ s__('ContextMenu|Delete') }}</span>
          <span class="menu-shortcut">Delete</span>
        </button>
        <div class="menu-divider"></div>
      </template>

      <!-- Formatting options (always shown - work with or without selection) -->
      <button class="menu-item" @click="$emit('format', 'bold')">
        <span class="menu-icon">**</span>
        <span>{{ s__('ContextMenu|Bold') }}</span>
        <span class="menu-shortcut">Ctrl+B</span>
      </button>
      <button class="menu-item" @click="$emit('format', 'italic')">
        <span class="menu-icon">_</span>
        <span>{{ s__('ContextMenu|Italic') }}</span>
        <span class="menu-shortcut">Ctrl+I</span>
      </button>
      <button class="menu-item" @click="$emit('format', 'underline')">
        <span class="menu-icon">__</span>
        <span>{{ s__('ContextMenu|Underline') }}</span>
        <span class="menu-shortcut">Ctrl+U</span>
      </button>
      <button class="menu-item" @click="$emit('format', 'strikethrough')">
        <span class="menu-icon">~</span>
        <span>{{ s__('ContextMenu|Strikethrough') }}</span>
        <span class="menu-shortcut">Ctrl+Shift+X</span>
      </button>
      <button class="menu-item" @click="$emit('format', 'spoiler')">
        <span class="menu-icon">||</span>
        <span>{{ s__('ContextMenu|Spoiler') }}</span>
        <span class="menu-shortcut">Ctrl+Shift+P</span>
      </button>
      <div class="menu-divider"></div>
      <button class="menu-item" @click="$emit('format', 'code')">
        <span class="menu-icon">`</span>
        <span>{{ s__('ContextMenu|Code') }}</span>
        <span class="menu-shortcut"></span>
      </button>
      <button class="menu-item" @click="$emit('format', 'monospace')">
        <span class="menu-icon">```</span>
        <span>{{ s__('ContextMenu|Monospace') }}</span>
        <span class="menu-shortcut">Ctrl+Shift+M</span>
      </button>
      <div class="menu-divider"></div>
      <button class="menu-item" @click="$emit('format', 'link')">
        <span class="menu-icon">🔗</span>
        <span>{{ s__('ContextMenu|Link') }}</span>
        <span class="menu-shortcut">Ctrl+K</span>
      </button>
      <button class="menu-item" @click="$emit('format', 'quotation')">
        <span class="menu-icon">></span>
        <span>{{ s__('ContextMenu|Quotation') }}</span>
        <span class="menu-shortcut">Ctrl+Shift+.</span>
      </button>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { s__ } from '@/locale'
import TrashIcon from './icons/TrashIcon.vue'

const props = defineProps({
  show: { type: [Boolean, Object], default: false },
  position: { type: Object, default: () => ({ x: 0, y: 0 }) },
  hasSelection: { type: [Boolean, Object], default: false },
})

const emit = defineEmits(['format', 'action'])

// Ensure we have proper boolean/object values (handles both refs and plain values)
const isVisible = computed(() => {
  const showValue = props.show?.value ?? props.show
  const posValue = props.position?.value ?? props.position
  return showValue === true && posValue && posValue.x > 0 && posValue.y > 0
})

// Unwrap hasSelection in case it's passed as a ref
const hasSelectionValue = computed(() => {
  return props.hasSelection?.value ?? props.hasSelection ?? false
})

// Compute style based on whether we're positioning above or below
const menuStyle = computed(() => {
  const pos = props.position?.value ?? props.position ?? { x: 0, y: 0 }
  if (pos.positionAbove) {
    // Position above: anchor bottom edge to the y coordinate
    return {
      left: `${pos.x}px`,
      bottom: `${window.innerHeight - pos.y}px`,
      top: 'auto',
    }
  } else {
    // Position below: anchor top edge to the y coordinate
    return {
      left: `${pos.x}px`,
      top: `${pos.y}px`,
      bottom: 'auto',
    }
  }
})
</script>

<style scoped>
.markdown-context-menu {
  position: fixed;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  min-width: 200px;
  z-index: 10000;
  font-size: 14px;
}

.menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 16px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  gap: 12px;
  color: #333;
  transition: background 0.15s;
  position: relative;
}

.menu-item:hover {
  background: #f0f0f0;
}

.menu-item:active {
  background: #e0e0e0;
}

.menu-icon {
  font-family: 'Courier New', monospace;
  font-weight: bold;
  min-width: 24px;
  text-align: center;
  color: #666;
}

.menu-shortcut {
  margin-left: auto;
  font-size: 12px;
  color: #999;
  font-family: 'Courier New', monospace;
}

.menu-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 4px 0;
}
</style>
